import { Calculator, Wallet } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { OrganizationScopePicker } from "@/modules/dashboard/payments-config/components/OrganizationScopePicker";
import { useOrganizationsCatalog } from "@/modules/dashboard/payments-config/hooks/useOrganizationsCatalog";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaPageHeader } from "@/shared/components/layout/ColocaPageHeader";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { AdjustBalanceDialog } from "../components/adjust/AdjustBalanceDialog";
import { CardWalletSummary } from "../components/CardWalletSummary";
import { CardWalletMovements } from "../components/history/CardWalletMovements";
import { RechargeQuoteDialog } from "../components/quote/RechargeQuoteDialog";
import { useCardWalletForOrganization } from "../hooks/useCardWalletForOrganization";
import { useCardWalletMovements } from "../hooks/useCardWalletMovements";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import type { AdjustDirection } from "../types/cardWallets.types";
import {
  adjustProviderTransactionId,
  ineligibilityFromProgram,
  ineligibilityFromWallet,
} from "../utils/cardWalletAdjust";

/**
 * Staff surface for the USD pool an organization loads its corporate cards from: what it holds, what moved,
 * and the two ways to move it.
 *
 * It opens on a picker rather than on a list because no endpoint can produce a cross-organization view of
 * these wallets — `GET /internal/wallets` filters by the caller's own organization, staff included. The
 * wallet is then read one organization at a time, and ONLY once its card program says this screen can serve
 * it: that read provisions a wallet when there is none, and for a Coloca Cards organization provisioning
 * reaches the card provider.
 */
export default function CardWalletsPage() {
  const { t } = useTranslations("cardWallets");
  const { user, can } = useAuth();
  const catalog = useOrganizationsCatalog();

  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [adjust, setAdjust] = useState<{ direction: AdjustDirection; nonce: number } | null>(null);
  const [highlightedReference, setHighlightedReference] = useState<string | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const { organization, isLoading: isResolvingOrganization } = useSelectedOrganization(
    organizationId,
    catalog.organizations,
  );

  // The program decides before anything is read. `null` here is also "not decided yet", which is why the
  // wallet read waits for the organization to resolve rather than firing on the raw id.
  const programIneligibility = organization ? ineligibilityFromProgram(organization.card_provider) : null;
  const mayReadWallet = organization !== null && programIneligibility === null;

  const wallet = useCardWalletForOrganization(mayReadWallet ? organizationId : null);
  const walletEntity = wallet.snapshot?.wallet ?? null;
  const walletIneligibility =
    programIneligibility ?? (walletEntity ? ineligibilityFromWallet(walletEntity) : null);

  const movements = useCardWalletMovements(walletEntity?.id ?? null);

  const highlightedProviderTxId = useMemo(() => {
    if (!highlightedReference || !walletEntity) return null;
    return adjustProviderTransactionId(walletEntity.id, highlightedReference);
  }, [highlightedReference, walletEntity]);

  const handleRefreshAll = useCallback(() => {
    wallet.refresh();
    movements.refresh();
  }, [wallet, movements]);

  const handleSeeMovement = useCallback(
    (reference: string) => {
      setAdjust(null);
      setHighlightedReference(reference);
      // The movement may be outside the current page or filtered out, so the list goes back to its widest
      // view instead of claiming there is nothing to see.
      movements.clearFilters();
      movements.refresh();
    },
    [movements],
  );

  const handleResetOrganization = useCallback(() => {
    setAdjust(null);
    setOrganizationId(null);
    setHighlightedReference(null);
  }, []);

  const openAdjust = useCallback((direction: AdjustDirection) => {
    setHighlightedReference(null);
    setAdjust({ direction, nonce: Date.now() });
  }, []);

  // The icon spins while either read is in flight, so the button reports what it is doing.
  const isRefreshing = (mayReadWallet && wallet.isLoading) || movements.isLoading;
  const balance = wallet.snapshot?.balance.current.balance ?? null;
  const pending = wallet.snapshot?.balance.pending.balance ?? null;
  const showsWalletRegion = organizationId !== null;

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 p-2">
      <ColocaPageHeader
        title={t("page_title")}
        description={t("page_subtitle")}
        actions={
          <>
            <ColocaButton
              type="button"
              variant="default"
              size="sm"
              icon={RefreshCw}
              aria-label={t("refresh_aria")}
              className={cn(isRefreshing && "[&_svg]:animate-spin")}
              disabled={organizationId === null}
              onClick={handleRefreshAll}
            >
              {t("refresh_button")}
            </ColocaButton>
            {/* Above the picker, and never disabled by it: the pool it quotes against is Coloca's own and
                backs every organization's cards, so the answer does not depend on who is selected. Its
                placement is the statement that this is page-level, not part of the organization's wallet. */}
            <ColocaButton
              type="button"
              variant="secondary"
              size="sm"
              icon={Calculator}
              onClick={() => setQuoteOpen(true)}
            >
              {t("quote_button")}
            </ColocaButton>
          </>
        }
      />

      <ColocaSurface variant="panel" padding="lg" className="max-w-2xl">
        <OrganizationScopePicker
          id="card-wallets-organization"
          mode="required"
          value={organizationId}
          onValueChange={(next) => {
            setOrganizationId(next);
            setHighlightedReference(null);
          }}
          catalog={catalog}
          labels={{
            label: t("field_organization"),
            placeholder: t("org_picker_placeholder"),
            loading: t("org_picker_loading"),
            empty: t("org_picker_empty"),
            searching: t("org_picker_searching"),
            searchError: t("org_picker_search_error"),
            catalogError: t("org_picker_catalog_error"),
            catalogTruncated: t("org_picker_catalog_truncated"),
            requiredHint: t("field_organization_required_hint"),
            orgHint: t("field_organization_hint"),
          }}
        />
      </ColocaSurface>

      {!showsWalletRegion ? (
        // Not a `ColocaEmpty`: that reads as "nothing found", and this is the normal starting point.
        <ColocaSurface variant="muted" padding="lg" className="border-dashed">
          <div className="flex flex-wrap items-start gap-5">
            <span
              aria-hidden="true"
              className="flex size-11 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-muted-foreground"
            >
              <Wallet className="size-5" />
            </span>
            <div className="flex min-w-0 max-w-[680px] flex-1 flex-col gap-1.5">
              <h2 className="text-base font-semibold tracking-tight">{t("landing_title")}</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">{t("landing_body")}</p>
            </div>
          </div>
        </ColocaSurface>
      ) : (
        <>
          <CardWalletSummary
            wallet={walletEntity}
            balance={balance}
            pending={pending}
            isLoading={isResolvingOrganization || (mayReadWallet && wallet.isLoading)}
            error={wallet.error}
            ineligibility={walletIneligibility}
            justCreated={wallet.snapshot?.justCreated ?? false}
            canAdjust={can("wallets:write")}
            onAdjust={openAdjust}
            onRetry={wallet.refresh}
          />

          {walletEntity && (
            <CardWalletMovements
              movements={movements.movements}
              total={movements.total}
              page={movements.page}
              onPageChange={movements.setPage}
              filters={movements.filters}
              onFiltersChange={movements.setFilters}
              onClearFilters={movements.clearFilters}
              isLoading={movements.isLoading}
              error={movements.error}
              onRetry={movements.refresh}
              currentUserId={user?.id ?? null}
              highlightedProviderTxId={highlightedProviderTxId}
            />
          )}
        </>
      )}

      {/* Mounted at the page root rather than inside the header, so its own state never reruns the header's
          render path. Kept mounted while closed: the pool read is gated on `open`, so there is nothing to
          tear down, and the typed amount survives a close-and-reopen during a call. */}
      <RechargeQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />

      {/* Mounted only while open and keyed on the attempt, so every step machine starts clean and the
          direction can never change under a form that is already filled in. */}
      {adjust && walletEntity && organization && (
        <AdjustBalanceDialog
          key={`${adjust.direction}-${adjust.nonce}`}
          open
          onOpenChange={(next) => {
            if (!next) setAdjust(null);
          }}
          direction={adjust.direction}
          organizationName={organization.name}
          wallet={walletEntity}
          balance={balance}
          onRefreshBalance={wallet.refresh}
          onSeeMovement={handleSeeMovement}
          onResetOrganization={handleResetOrganization}
          onRecorded={movements.refresh}
        />
      )}
    </div>
  );
}
